import type { PipelineVerboseStep } from "@/features/chat/lib/turnStream";

/**
 * The elapsed time of a running turn leaves out the time it spent waiting on the user's
 * clarification answer: that wait is the reader's, not the turn's work. The clock stops when the
 * turn starts waiting and goes on from the same reading once the answer is in.
 *
 * Live, the browser marks both moments. A turn loaded from history reads them off its steps'
 * timestamps instead: each wait runs from the "Waiting" step to the first step after it, which is
 * the resumed run's.
 */

const WAITING_FOR_CLARIFICATION_EVENT = "waiting_for_clarification";

export type TurnClock = {
  /** Time spent waiting on clarification answers so far. */
  clarificationWaitMs?: number;
  /** When the current wait began; set only while the turn waits, which stops the clock. */
  clockPausedAt?: number;
};

/** The turn waits on the user: the clock stops. A second pause keeps the first's start. */
export function pauseTurnClock(clock: TurnClock, at: number): void {
  if (clock.clockPausedAt === undefined) {
    clock.clockPausedAt = at;
  }
}

/** The user answered: the clock goes on, leaving the wait out. */
export function resumeTurnClock(clock: TurnClock, at: number): void {
  if (clock.clockPausedAt === undefined) {
    return;
  }
  clock.clarificationWaitMs =
    (clock.clarificationWaitMs ?? 0) + Math.max(0, at - clock.clockPausedAt);
  clock.clockPausedAt = undefined;
}

/**
 * A stored turn's clock, from its steps' timestamps. A wait with no step after it is still open
 * only while the turn waits; on a turn that has moved on, the resumed run has not stored a step
 * yet and when it started is unknown, so that wait is left in.
 */
export function turnClockFromSteps(steps: PipelineVerboseStep[], isWaiting: boolean): TurnClock {
  let clarificationWaitMs = 0;
  let pausedAt: number | null = null;
  for (const step of steps) {
    if (step.createdAt === undefined) {
      continue;
    }
    if (step.eventType === WAITING_FOR_CLARIFICATION_EVENT) {
      pausedAt ??= step.createdAt;
    } else if (pausedAt !== null) {
      clarificationWaitMs += Math.max(0, step.createdAt - pausedAt);
      pausedAt = null;
    }
  }
  return {
    ...(clarificationWaitMs > 0 ? { clarificationWaitMs } : {}),
    ...(isWaiting && pausedAt !== null ? { clockPausedAt: pausedAt } : {}),
  };
}
